import { ApiRequest } from "@/api/types";
import { computed, ref, InjectionKey } from "@vue/composition-api";
import { formConfig, initialPartnerUser, ValidateKey } from "../partnerUser";
import { useValidate } from "./useValidate";
import {
  getManagedPartnerUser as getPartnerUserRequest,
  createManagedPartnerUser as createPartnerUserRequest,
  updateManagedPartnerUser as updatePartnerUserRequest
} from "@/api";

export type ValidateSubKey = string | number | undefined;
export type partnerUserError = {
  key: ValidateKey;
  subKey: ValidateSubKey;
  message: string;
};

/**
 * フロントでチェックするバリデーションエラーのインスタンス
 */
export class ValidationError extends Error {
  errors: partnerUserError[];
  constructor(errors: partnerUserError[], message?: string) {
    super(message);
    this.errors = errors;
  }
}

export type CreatePartnerUserStore = ReturnType<typeof useCreatePartnerUser>;
export const CREATE_PARTNER_USER_STORE_KEY: InjectionKey<CreatePartnerUserStore> = Symbol(
  "CREATE_PARTNER_USER_STORE_KEY"
);

/**
 * PartnerUserの作成情報、エラー情報をグローバルで保持する
 */
export const useCreatePartnerUser = () => {
  const newPartnerUser = ref<ApiRequest.CreateManagedPartnerUser>();
  const partnerUserErrors = ref<partnerUserError[]>([]);

  const {
    formErrors,
    getFirstErrorMessage,
    resetErrorMessage,
    validate
  } = useValidate();

  const partnerUserValidate = (key: ValidateKey, subKey?: ValidateSubKey) => {
    if (!newPartnerUser.value) return;
    switch (key) {
      case "name": {
        const error = validate(
          newPartnerUser.value.name,
          formConfig[key],
          undefined,
          formConfig[key].label
        );
        setPartnerUserErrors(key, subKey, error[0]?.message);
        return error[0]?.message;
      }
      case "email": {
        const error = validate(
          newPartnerUser.value.email,
          formConfig[key],
          undefined,
          formConfig[key].label
        );
        setPartnerUserErrors(key, subKey, error[0]?.message);
        return error[0]?.message;
      }
    }
  };

  const partnerUserValidateAll = () => {
    partnerUserValidate("name");
    partnerUserValidate("email");
    partnerUserValidate("password");
  };

  const isAdmin = computed<boolean>(() => {
    if (!newPartnerUser.value) return false;
    return newPartnerUser.value.admin;
  });

  const hasError = computed<boolean>(() => {
    resetPartnerUserErrors();
    partnerUserValidateAll();
    return partnerUserErrors.value.length > 0;
  });

  const setPartnerUserErrors = (
    key: ValidateKey,
    subKey?: ValidateSubKey,
    message?: string
  ) => {
    const nextErrors = partnerUserErrors.value.filter(error =>
      subKey ? error.key !== key || error.subKey !== subKey : error.key !== key
    );

    if (message) {
      nextErrors.push({ key, message, subKey });
    }

    partnerUserErrors.value = nextErrors;
  };

  const resetPartnerUserErrors = () => {
    partnerUserErrors.value = [];
  };

  const getPartnerUserErrorMessage = (
    key: ValidateKey,
    subKey?: string | number
  ) => {
    const error = partnerUserErrors.value.find(error =>
      subKey ? error.key === key && error.subKey === subKey : error.key === key
    );

    return error?.message;
  };

  const submitting = ref(false);
  const createPartnerUser = async () => {
    if (!newPartnerUser.value) return;

    try {
      submitting.value = true;

      resetPartnerUserErrors();
      partnerUserValidateAll();
      if (hasError.value) return;

      await createPartnerUserRequest(newPartnerUser.value);
      setPartnerUser(initialPartnerUser);
    } catch (error) {
      throw error;
    } finally {
      submitting.value = false;
    }
  };

  const loading = computed<boolean>(() => submitting.value);

  const setPartnerUser = (
    partnerUser: ApiRequest.CreateManagedPartnerUser,
    validateKey?: { key: ValidateKey; subKey?: ValidateSubKey }
  ) => {
    newPartnerUser.value = partnerUser;
    if (validateKey) {
      partnerUserValidate(validateKey.key, validateKey.subKey);
    }
  };

  return {
    loading,
    isAdmin,
    hasError,
    newPartnerUser,
    createPartnerUser,
    setPartnerUser,
    partnerUserErrors,
    partnerUserValidate,
    setPartnerUserErrors,
    resetPartnerUserErrors,
    getPartnerUserErrorMessage,
    formErrors,
    getFirstErrorMessage,
    resetErrorMessage,
    validate
  } as const;
};

export type UpdatePartnerUserStore = ReturnType<typeof useUpdatePartnerUser>;
export const UPDATE_PARTNER_USER_STORE_KEY: InjectionKey<UpdatePartnerUserStore> = Symbol(
  "UPDATE_PARTNER_USER_STORE_KEY"
);

/**
 * PartnerUserの編集情報、エラー情報をグローバルで保持する
 */
export const useUpdatePartnerUser = (partnerUserId: number) => {
  const editingPartnerUser = ref<ApiRequest.UpdateManagedPartnerUser>();
  const partnerUserErrors = ref<partnerUserError[]>([]);

  const {
    formErrors,
    getFirstErrorMessage,
    resetErrorMessage,
    validate
  } = useValidate();

  const partnerUserValidate = (key: ValidateKey, subKey?: ValidateSubKey) => {
    if (!editingPartnerUser.value) return;
    switch (key) {
      case "name": {
        const error = validate(
          editingPartnerUser.value.name,
          formConfig[key],
          undefined,
          formConfig[key].label
        );
        setPartnerUserErrors(key, subKey, error[0]?.message);
        return error[0]?.message;
      }
      case "email": {
        const error = validate(
          editingPartnerUser.value.email,
          formConfig[key],
          undefined,
          formConfig[key].label
        );
        setPartnerUserErrors(key, subKey, error[0]?.message);
        return error[0]?.message;
      }
      case "password": {
        const error = validate(
          editingPartnerUser.value.password,
          formConfig[key],
          undefined,
          formConfig[key].label
        );
        setPartnerUserErrors(key, subKey, error[0]?.message);
        return error[0]?.message;
      }
    }
  };

  const partnerUserValidateAll = () => {
    partnerUserValidate("name");
    partnerUserValidate("email");
    partnerUserValidate("password");
  };

  const isAdmin = computed<boolean>(() => {
    if (!editingPartnerUser.value) return false;
    return editingPartnerUser.value.admin;
  });

  const hasError = computed<boolean>(() => {
    resetPartnerUserErrors();
    partnerUserValidateAll();
    return partnerUserErrors.value.length > 0;
  });

  const setPartnerUserErrors = (
    key: ValidateKey,
    subKey?: ValidateSubKey,
    message?: string
  ) => {
    const nextErrors = partnerUserErrors.value.filter(error =>
      subKey ? error.key !== key || error.subKey !== subKey : error.key !== key
    );

    if (message) {
      nextErrors.push({ key, message, subKey });
    }

    partnerUserErrors.value = nextErrors;
  };

  const resetPartnerUserErrors = () => {
    partnerUserErrors.value = [];
  };

  const getPartnerUserErrorMessage = (
    key: ValidateKey,
    subKey?: string | number
  ) => {
    const error = partnerUserErrors.value.find(error =>
      subKey ? error.key === key && error.subKey === subKey : error.key === key
    );

    return error?.message;
  };

  const fetching = ref(false);
  const refreshEditingPartnerUser = async () => {
    try {
      fetching.value = true;
      const { data: partnerUser } = await getPartnerUserRequest({
        partner_user_id: partnerUserId
      });

      if (partnerUser) {
        setPartnerUser({
          partner_user_id: partnerUser.id,
          name: partnerUser.name,
          email: partnerUser.email,
          password: "",
          admin: partnerUser.admin
        });
      }
    } finally {
      fetching.value = false;
    }
  };

  const submitting = ref(false);
  const updatePartnerUser = async () => {
    if (!editingPartnerUser.value) return;

    try {
      submitting.value = true;

      resetPartnerUserErrors();
      partnerUserValidateAll();
      if (hasError.value) return;

      await updatePartnerUserRequest(editingPartnerUser.value);
      await refreshEditingPartnerUser();
    } catch (error) {
      throw error;
    } finally {
      submitting.value = false;
    }
  };

  const loading = computed<boolean>(() => fetching.value || submitting.value);

  const setPartnerUser = (
    partnerUser: ApiRequest.UpdateManagedPartnerUser,
    validateKey?: { key: ValidateKey; subKey?: ValidateSubKey }
  ) => {
    editingPartnerUser.value = partnerUser;
    if (validateKey) {
      partnerUserValidate(validateKey.key, validateKey.subKey);
    }
  };

  return {
    loading,
    isAdmin,
    hasError,
    editingPartnerUser,
    refreshEditingPartnerUser,
    updatePartnerUser,
    setPartnerUser,
    partnerUserErrors,
    partnerUserValidate,
    setPartnerUserErrors,
    resetPartnerUserErrors,
    getPartnerUserErrorMessage,
    formErrors,
    getFirstErrorMessage,
    resetErrorMessage,
    validate
  } as const;
};
